import { Box, Typography, Grid2 as Grid } from '@mui/material';
import { useStylesProductPageUXB } from '../SharedStyles/SharedStyles';
import { EventProduct, ProductType } from 'types/Products';
import { CategoryDetail, SearchCategoryType } from 'types/Search';
import { staticContent } from 'components/constants/Constants';
import {
  getPropertyValuesByType,
  getValueStrings,
  getValueStringsCaptalized
} from '../helpers';
import { UxBeautyPrivateAttributes } from 'types/Events';
import { t } from 'i18next';
import { getUxbPrivateAttributes } from 'components/pages/products/helpers/formAttributes';
import { useIfVerifiedUser } from 'hooks/useUserState_utils';

const ProductInformation = ({ product }: { product: EventProduct }) => {
  const { classes } = useStylesProductPageUXB();
  const isVerifiedUser = useIfVerifiedUser();

  const productData = productDetails(product, isVerifiedUser).filter(
    el => el.value
  );
  if (!productData.length) return null;
  return (
    <Box className={classes.productInformationGrid}>
      <Typography
        variant="h3"
        sx={{
          color: 'GrayText'
        }}
      >
        Product information
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {productData.map((detail: any, key: number) => {
          if (!detail.value) return null;
          return (
            <Grid
              key={key}
              container
              spacing={1}
              sx={{
                paddingBottom: 3,
                borderBottom: '1px solid black'
              }}
            >
              <Grid size={{ sm: 3 }}>
                <Typography
                  sx={{
                    color: 'GrayText'
                  }}
                >
                  {detail.title}
                </Typography>
              </Grid>
              <Grid size={{ sm: 9 }}>
                {detail?.title === 'Certifications' ? (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {detail?.value.map((img: string, key: number) => {
                      return (
                        <img
                          key={key}
                          src={img}
                          style={{
                            height: '40px',
                            width: 'auto',
                            objectFit: 'contain'
                          }}
                          alt={`Certification`}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography>{detail.value}</Typography>
                )}
              </Grid>
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProductInformation;

type FieldDef = {
  title: string;
  value: string | string[] | undefined;
  isPrivate?: boolean;
};

const productDetails = (
  product: EventProduct,
  privateDetailsIncluded: boolean
) => {
  const certField: FieldDef = {
    title: 'Certifications',
    value: product?.categories?.technical?.certifications
      ? getUXBCertificationsImgs(product?.categories?.technical?.certifications)
      : ''
  };
  return [
    ...(product.type === ProductType.INGREDIENTS
      ? ingredientFields(product)
      : conceptsFields(product)),
    ...commonFields(product),
    ...privateDataFields(product),
    certField
  ].filter(field => {
    if (privateDetailsIncluded) return true;
    return !field.isPrivate;
  });
};

// Field definitions
const commonFields = (product: EventProduct): FieldDef[] => [
  {
    //value/prop
    title: 'Claims',
    value: product?.categories?.technical?.claims
      ? getPropertyValuesByType(
          product.categories.technical.claims,
          SearchCategoryType.PERFORMANCE
        )
      : ''
  },
  {
    title: 'Sustainability claims',
    value: product?.categories?.technical?.claims
      ? getPropertyValuesByType(
          product.categories.technical.claims,
          SearchCategoryType.SUSTAINABILITY
        )
      : ''
  },
  {
    //value/type
    title: 'Chemical properties',
    value: product?.categories?.technical?.properties
      ? getPropertyValuesByType(
          product.categories.technical.properties,
          SearchCategoryType.CHEMICAL
        )
      : ''
  },
  {
    //value/type
    title: 'Physical properties',
    value: product?.categories?.technical?.properties
      ? getPropertyValuesByType(
          product.categories.technical.properties,
          SearchCategoryType.PHYSICAL
        )
      : ''
  },
  {
    //array of values
    title: 'Applications',
    value: product?.categories?.technical?.applications
      ? getValueStrings(product.categories.technical.applications)
      : ''
  },
  {
    //value/type
    title: 'Color',
    value: product?.categories?.technical?.properties
      ? getPropertyValuesByType(
          product.categories.technical.properties,
          SearchCategoryType.COLOR
        )
      : ''
  },
  {
    //value/type
    title: 'Odor',
    value: product?.categories?.technical?.properties
      ? getPropertyValuesByType(
          product?.categories?.technical?.properties,
          SearchCategoryType.ODOR
        )
      : ''
  },
  {
    //value/type
    title: 'Appearance',
    value: product?.categories?.technical?.properties
      ? getPropertyValuesByType(
          product.categories.technical.properties,
          SearchCategoryType.APPEARANCES
        )
      : ''
  }
];
const ingredientFields = (product: EventProduct): FieldDef[] => [
  {
    //string (common ings and concepts)
    title: 'INCI',
    value: (product?.identifiers?.inci ?? []).join(', '),
    isPrivate: true
  },
  {
    //string
    title: 'IUPAC',
    value: (product?.identifiers?.iupac ?? []).join(', '),
    isPrivate: true
  },
  {
    //string
    title: 'CAS',
    value: (product?.identifiers?.cas ?? []).join(', '),
    isPrivate: true
  },
  {
    //string
    title: 'EC',
    value: (product?.identifiers?.ec ?? []).join(', ')
  },
  {
    //string
    title: 'Usage level',
    value: product?.categories?.technical?.usagelevel?.value
  },

  {
    //array of values
    title: 'Functions',
    value: product?.categories?.technical?.functions
      ? getValueStrings(product.categories.technical.functions)
      : ''
  },
  {
    //array of value
    title: 'Origin',
    value: product?.categories?.technical?.origin
      ? getValueStringsCaptalized(product.categories.technical.origin)
      : ''
  },
  {
    //array of value
    title: 'Origin species',
    value: product?.categories?.technical?.['origin-species']
      ? getValueStrings(product.categories.technical?.['origin-species'])
      : ''
  },
  {
    //array of value
    title: 'Chemical group',
    value: product?.categories?.technical?.chemicalgroup
      ? getValueStrings(product.categories.technical.chemicalgroup)
      : ''
  }
];
const conceptsFields = (_: EventProduct): FieldDef[] => [];

const privateDataFields = (product: EventProduct): FieldDef[] =>
  //these are all string types, either, boolean string multivalue string  or singlevalue string
  (product.type === ProductType.INGREDIENTS
    ? [
        UxBeautyPrivateAttributes.AWARDS,
        UxBeautyPrivateAttributes.MARKET_TRENDS,
        UxBeautyPrivateAttributes.TECHNOLOGY,
        UxBeautyPrivateAttributes.PRESERVATIVE,
        UxBeautyPrivateAttributes.COUNTRY_ORIGIN,
        UxBeautyPrivateAttributes.CLINICAL_TIME_DELIVERY,
        UxBeautyPrivateAttributes.BIOLOGICAL_TARGET,
        UxBeautyPrivateAttributes.LIFE_CYCLE_ANALYSIS,
        UxBeautyPrivateAttributes.STORAGE_CONDITIONS,
        UxBeautyPrivateAttributes.PUBLICATIONS,
        UxBeautyPrivateAttributes.PH_STABILITY_RANGE,
        UxBeautyPrivateAttributes.TEMPERATURE_STABILITY,
        UxBeautyPrivateAttributes.FORMULATION_TIPS
      ]
    : [
        UxBeautyPrivateAttributes.MARKET_TRENDS,
        UxBeautyPrivateAttributes.PRODUCTION_SITE,
        UxBeautyPrivateAttributes.CONSUMER_TESTS,
        UxBeautyPrivateAttributes.POSITIONING,
        UxBeautyPrivateAttributes.SAMPLE_UNIT_PRICE,
        UxBeautyPrivateAttributes.SAMPLE_UNIT_SIZE,
        UxBeautyPrivateAttributes.STORAGE_CONDITIONS,
        UxBeautyPrivateAttributes.PH_STABILITY_RANGE,
        UxBeautyPrivateAttributes.VISCOSITY
      ]
  ).map(el => ({
    //string
    title: t(`forms.product.${el}.label`),
    value: (() => {
      const data = product?.event?.product_private_attributes?.data?.[el];
      return data ? getPrivateAttributeValue(el, product.type, data) : '';
    })(),
    isPrivate: !!getUxbPrivateAttributes(product.type).find(a => a.key === el)
      ?.isPrivate
  }));

const getUXBCertificationsImgs = (certifications: CategoryDetail[]) => {
  const imgsList: string[] = [];
  certifications.forEach(certification => {
    imgsList.push(
      `${staticContent + '/certifications/' + certification.value}`
    );
  });
  return imgsList;
};

//TODO: move this to a more appropriate higher place
export const getPrivateAttributeValue = (
  attribute: UxBeautyPrivateAttributes,
  productType: ProductType,
  data: string[]
) => {
  const dataType = getUxbPrivateAttributes(productType).find(
    a => a.key === attribute
  )?.type;
  switch (dataType) {
    case 'multiSelect':
    case 'singleSelect':
    case 'string':
      return data?.join(', ');
    case 'boolean':
      if (data?.[0] === 'true') {
        return 'Yes';
      } else if (data?.[0] === 'false') {
        return 'No';
      } else return '';

    default:
      return '';
  }
};
